import React, { useState, useEffect } from "react";
import {
  Box,
  SimpleGrid,
  useColorMode,
  Button,
  VStack,
  Wrap,
  WrapItem,
  useToast,
  Text,
} from "@chakra-ui/react";
import { Link, useHistory, useLocation } from "react-router-dom";
import ItineraryCard from "./ItineraryCard";

import axiosInstance from "../../helpers/axiosInstance";
import NoBucketListMessage from "./NoBucketListMessage"; // Import the new component
import Translate from "../../components/LanguageTranslation/Translate";

import ActivityCard from "./Activity/ActivityCard";
import AddActivity from "./Activity/AddActivity";
import AccommodationCard from "./Accommodation/AccommodationCard";
import AddAccommodation from "./Accommodation/AddAccommodation";
import FlightsCard from "./Flight/FlightsCard";
import AddFlight from "./Flight/AddFlight";
import RVsCard from "./RV/RVsCard";
import AddRV from "./RV/AddRV";
import { Helmet } from "react-helmet-async";

import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import Loading from "../common/Loading";
import BucketHeader from "./BucketHeader";

import {
  ITINERARY_SUBS,
  TAG_MAPPING,
  EXPLORE_SUBS,
  ACCOMMODATION_SUBS,
  TRANSPORT_SUBS,
} from "./bucketConstants";
import CategoryFilterMenu from "./CategoryFilterMenu";
import AddRentalCar from "./RentalCar/AddRentalCar";
import RentalCarCard from "./RentalCar/RentalCarCard";
import AddTrainTickets from "./TrainTickets/AddTrainTickets";
import TrainTicketsCard from "./TrainTickets/TrainTicketsCard";
import AddBusTicket from "./BusTicket/AddBusTicket";
import BusTicketCard from "./BusTicket/BusTicketCard";
import AddOtherTransporation from "./OtherTransporation/AddOtherTransporation";
import OtherTransporationCard from "./OtherTransporation/OtherTransporationCard";

const BucketList = () => {
  const history = useHistory();
  const location = useLocation();

  const { colorMode } = useColorMode();
  const [selectedTag, setSelectedTag] = useState("all");
  const [ongoingTrip, setOngoingTrip] = useState(null);
  const [data, setData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [tripToDelete, setTripToDelete] = useState(null);
  const [itinSelections, setItinSelections] = useState([]);
  const [exploreSelections, setExploreSelections] = useState([]); // stores pref strings
  const [accSelections, setAccSelections] = useState([]);
  const [transportPref, setTransportPref] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editRentalCar, setEditRentalCar] = useState(null);
  const [editTrainTicket, setEditTrainTicket] = useState(null);
  const [editBusTicket, setEditBusTicket] = useState(null);
  const [editAccommodation, setEditAccommodation] = useState(null);
  const [editFlight, setEditFlight] = useState(null);
  const [editActivity, setEditActivity] = useState(null);
  const [editRV, setEditRV] = useState(null);
  const [editOtherTransporation, setEditOtherTransporation] = useState(null);
  const cancelRef = React.useRef();
  const toast = useToast();

  const userEmail = localStorage.getItem("email");

  // Reflect ?filter=...&status=... into React state
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filterQ = params.get("filter") || "all";
    const statusQ = params.get("status") || "";

    // Tag
    setSelectedTag(filterQ);

    // Only itineraries use the "status" param
    if (filterQ === "itineraries") {
      const normalized = statusQ.toLowerCase();
      if (["shared", "personal", "all"].includes(normalized)) {
        setItinSelections(
          normalized === "all" ? [] : [normalized] // empty array == no filter
        );
      }
    } else {
      setItinSelections([]);
    }
  }, [location.search]);

  const pushURL = (filter, status = "") => {
    const params = new URLSearchParams();
    params.set("filter", filter);
    if (filter === "itineraries" && status) params.set("status", status);
    history.replace({
      pathname: "/ai-travel-planner/bucket-list",
      search: params.toString(),
    });
  };

  const fetchTrips = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get("/bucket-list/");
      const trips = response.data.data;
      // Find the ongoing trip
      const ongoing = trips.find(
        (trip) =>
          trip.allowed_users?.filter((u) => u.email === userEmail)[0].is_ongoing
      );

      // Set the ongoing trip if it exists
      setOngoingTrip(ongoing ? ongoing : null);

      setData(trips);
    } catch (error) {
      console.error("Error fetching trip data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fix: Only wrap in data for 'explore', otherwise add as-is
  const addTripItem = (item) => {
    if (item.category === "explore") {
      setData([
        ...data,
        {
          ...item,
          data: { ...item },
        },
      ]);
    } else {
      setData([...data, item]);
    }
  };

  useEffect(() => {
    fetchTrips();
  }, []);

  const handleToggleOngoingTrip = async (itinerary) => {
    // event.stopPropagation();

    const newOngoingTripStatus = !itinerary.allowed_users.filter(
      (u) => u.email === userEmail
    )[0].is_ongoing;

    try {
      const response = await axiosInstance.put(
        `/bucket-list/${itinerary._id}/update/status`,
        { is_ongoing: newOngoingTripStatus } // Pass the new is_ongoing value
      );

      if (response.data.success) {
        // Toggle the local data and ongoing trip status based on completion
        const updatedData = data.map((item) =>
          item._id === itinerary._id
            ? {
                ...item,
                allowed_users: item.allowed_users.map((user) =>
                  user.email === userEmail // Check if the user's email matches
                    ? { ...user, is_ongoing: newOngoingTripStatus } // Update is_ongoing
                    : user
                ),
              }
            : item
        );

        setData(updatedData);

        if (!newOngoingTripStatus && itinerary._id === ongoingTrip?._id) {
          setOngoingTrip(null);
        } else if (newOngoingTripStatus) {
          setOngoingTrip(itinerary);
        }
      }
    } catch (error) {
      console.error("Error updating trip completion:", error);
    }
  };

  const handleDelete = (e, tripId) => {
    e.stopPropagation();
    setTripToDelete(tripId);
    setIsAlertOpen(true);
  };

  const confirmDelete = () => {
    axiosInstance
      .delete(`/bucket-list/${tripToDelete}/delete`)
      .then((response) => {
        // Handle successful deletion
        setData(data.filter((item) => item._id !== tripToDelete));
        if (ongoingTrip && ongoingTrip._id === tripToDelete) {
          setOngoingTrip(null);
        }
      })
      .catch((error) => {
        // Handle error
        console.error("Error deleting itinerary:", error);
        toast({
          title: "Error deleting item",
          description:
            error?.response?.data?.message ||
            "An error occurred while deleting the item.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      })
      .finally(() => {
        setIsAlertOpen(false);
        setTripToDelete(null);
      });
  };

  const handleEditRentalCar = (rentalCar, itemId) => {
    setEditRentalCar({ ...rentalCar, _id: itemId });
    setIsModalOpen(true);
  };

  const handleEditSuccess = (updatedRentalCar) => {
    // Update the data array with the edited rental car
    setData(
      data.map((item) =>
        item._id === editRentalCar._id
          ? { ...item, data: updatedRentalCar.data }
          : item
      )
    );
    setEditRentalCar(null);
  };

  const handleEditTrainTicket = (trainTicket, itemId) => {
    setEditTrainTicket({ ...trainTicket, _id: itemId });
    setIsModalOpen(true);
  };

  const handleTrainTicketEditSuccess = (updatedTrainTicket) => {
    // Update the data array with the edited train ticket
    setData(
      data.map((item) =>
        item._id === editTrainTicket._id
          ? { ...item, data: updatedTrainTicket.data }
          : item
      )
    );
    setEditTrainTicket(null);
  };

  const handleEditBusTicket = (busTicket, itemId) => {
    setEditBusTicket({ ...busTicket, _id: itemId });
    setIsModalOpen(true);
  };

  const handleBusTicketEditSuccess = (updatedBusTicket) => {
    // Update the data array with the edited bus ticket
    setData(
      data.map((item) =>
        item._id === editBusTicket._id
          ? { ...item, data: updatedBusTicket.data }
          : item
      )
    );
    setEditBusTicket(null);
  };

  const handleEditAccommodation = (accommodation, itemId) => {
    setEditAccommodation({ ...accommodation, _id: itemId });
    setIsModalOpen(true);
  };

  const handleAccommodationEditSuccess = (updatedAccommodation) => {
    // Update the data array with the edited accommodation
    setData(
      data.map((item) =>
        item._id === editAccommodation._id
          ? { ...item, data: updatedAccommodation }
          : item
      )
    );
    setEditAccommodation(null);
  };

  const handleEditFlight = (flight, itemId) => {
    setEditFlight({ ...flight, _id: itemId });
    setIsModalOpen(true);
  };

  const handleFlightEditSuccess = (updatedFlight) => {
    // Update the data array with the edited flight
    setData(
      data.map((item) =>
        item._id === editFlight._id ? { ...item, data: updatedFlight } : item
      )
    );
    setEditFlight(null);
  };

  const handleEditActivity = (activity, itemId) => {
    setEditActivity({ ...activity, _id: itemId });
    setIsModalOpen(true);
  };

  const handleActivityEditSuccess = (updatedActivity) => {
    // Update the data array with the edited activity
    setData(
      data.map((item) =>
        item._id === editActivity._id
          ? { ...item, data: updatedActivity }
          : item
      )
    );
    setEditActivity(null);
  };

  const handleEditRV = (rv, itemId) => {
    setEditRV({ ...rv, _id: itemId });
    setIsModalOpen(true);
  };

  const handleRVEditSuccess = (updatedRV) => {
    // Update the data array with the edited RV
    setData(
      data.map((item) =>
        item._id === editRV._id ? { ...item, data: updatedRV } : item
      )
    );
    setEditRV(null);
  };

  const handleEditOtherTransporation = (otherTransporation, itemId) => {
    setEditOtherTransporation({ ...otherTransporation, _id: itemId });
    setIsModalOpen(true);
  };

  const handleOtherTransporationEditSuccess = (updatedOtherTransporation) => {
    setData(
      data.map((item) =>
        item._id === editOtherTransporation._id
          ? { ...item, data: updatedOtherTransporation.data }
          : item
      )
    );
    setEditOtherTransporation(null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditRentalCar(null);
    setEditTrainTicket(null);
    setEditBusTicket(null);
    setEditAccommodation(null);
    setEditFlight(null);
    setEditActivity(null);
    setEditRV(null);
    setEditOtherTransporation(null);
  };

  // Enhanced applySubs: treat status="all" as no filter
  const applySubs = (items, selections, allSubs, field = "preference") => {
    const prefs = selections.map((s) => s.toLowerCase());

    // 1) If they clicked "Select All", just return every item immediately:
    if (prefs.includes("all")) {
      return items;
    }

    // 2) For itineraries/status – your existing all-status logic:
    if (field === "status" && prefs.includes("all")) {
      return items;
    }
    // existing "other" logic ...
    if (field === "preference" && prefs.includes("other")) {
      const nonOther = allSubs
        .filter((s) => s.pref.toLowerCase() !== "other")
        .map((s) => s.pref.toLowerCase());
      return items.filter((it) => !nonOther.includes(it[field]?.toLowerCase()));
    }
    return items.filter((it) => prefs.includes(it[field]?.toLowerCase()));
  };

  // Final filter pass
  const filterItems = (tag) => {
    const mapped = TAG_MAPPING[tag];
    if (mapped === "all") return data;

    let items = data.filter((it) => it.category === mapped);

    if (tag === "itineraries" && itinSelections.length) {
      items = applySubs(items, itinSelections, ITINERARY_SUBS, "status");
    }
    if (tag === "transportation" && transportPref.length) {
      items = applySubs(items, transportPref, TRANSPORT_SUBS);
    }
    if (tag === "explore" && exploreSelections.length) {
      items = applySubs(items, exploreSelections, EXPLORE_SUBS);
    }
    if (tag === "accommodation" && accSelections.length) {
      items = applySubs(items, accSelections, ACCOMMODATION_SUBS);
    }
    return items;
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  // helper: which transportation modal should we open?
  const renderTransportationModal = () => {
    if (transportPref.includes("rv")) {
      return (
        <AddRV
          isOpen={isModalOpen}
          onClose={closeModal}
          addTripItem={addTripItem}
          editItem={editRV}
          onEditSuccess={handleRVEditSuccess}
        />
      );
    }

    if (transportPref.includes("rental_car")) {
      return (
        <AddRentalCar
          isOpen={isModalOpen}
          onClose={closeModal}
          addTripItem={addTripItem}
          defaultSubcategories={transportPref}
          editItem={editRentalCar}
          onEditSuccess={handleEditSuccess}
        />
      );
    }

    if (transportPref.includes("train")) {
      return (
        <AddTrainTickets
          isOpen={isModalOpen}
          onClose={closeModal}
          addTripItem={addTripItem}
          defaultSubcategories={transportPref}
          editItem={editTrainTicket}
          onEditSuccess={handleTrainTicketEditSuccess}
        />
      );
    }

    if (transportPref.includes("bus")) {
      return (
        <AddBusTicket
          isOpen={isModalOpen}
          onClose={closeModal}
          addTripItem={addTripItem}
          defaultSubcategories={transportPref}
          editItem={editBusTicket}
          onEditSuccess={handleBusTicketEditSuccess}
        />
      );
    }
    if (transportPref.includes("other")) {
      return (
        <AddOtherTransporation
          isOpen={isModalOpen}
          onClose={closeModal}
          addTripItem={addTripItem}
          defaultSubcategories={transportPref}
          editItem={editOtherTransporation}
          onEditSuccess={handleOtherTransporationEditSuccess}
        />
      );
    }

    return (
      <AddFlight
        isOpen={isModalOpen}
        onClose={closeModal}
        addTripItem={addTripItem}
        editItem={editFlight}
        onEditSuccess={handleFlightEditSuccess}
      />
    );
  };
  const renderAddModal = () => {
    if (!isModalOpen) return null; // nothing to show

    if (selectedTag === "transportation") return renderTransportationModal();
    if (selectedTag === "accommodation")
      return (
        <AddAccommodation
          isOpen={isModalOpen}
          onClose={closeModal}
          addTripItem={addTripItem}
          defaultSubcategories={accSelections}
          editItem={editAccommodation}
          onEditSuccess={handleAccommodationEditSuccess}
        />
      );
    if (selectedTag === "explore")
      return (
        <AddActivity
          isOpen={isModalOpen}
          onClose={closeModal}
          addTripItem={addTripItem}
          defaultSubcategories={exploreSelections}
          editItem={editActivity}
          onEditSuccess={handleActivityEditSuccess}
        />
      );

    /* itineraries / all / other tags → no modal */
    return null;
  };

  // ---------- one-liner reset map ----------
  const resetters = {
    transportation: setTransportPref,
    explore: setExploreSelections,
    accommodation: setAccSelections,
  };

  // clear every group except the one we're keeping
  const clearSelectionsExcept = (keep) =>
    Object.entries(resetters).forEach(([key, reset]) => {
      if (key !== keep) reset([]); // wipe the array
    });

  const subCount =
    selectedTag === "transportation"
      ? transportPref.length
      : selectedTag === "explore"
      ? exploreSelections.length
      : selectedTag === "accommodation"
      ? accSelections.length
      : selectedTag === "itineraries"
      ? itinSelections.length
      : 0;
  const showAddButton = subCount === 1;

  if (isLoading) {
    return <Loading fullScreen />;
  }

  // Check if user is coming from chat
  const params = new URLSearchParams(location.search);
  const fromChat = params.get("fromChat");

  // If no data, show only the NoBucketListMessage (unless coming from chat)
  if (data.length === 0 && fromChat !== "true") {
    return <NoBucketListMessage fetchTrips={fetchTrips} />;
  }

  return (
    <>
      <Helmet>
        <title>Bucket List&nbsp;| NOMAD360</title>
      </Helmet>

      <Box
        h={{ base: "calc(100vh - 64px)", md: "100vh", lg: "100vh" }}
        overflowY="hidden"
      >
        <BucketHeader colorMode={colorMode} fetchTrips={fetchTrips} />
        <VStack
          align="left"
          h={{ base: "calc(92vh - 64px)", md: "92vh", lg: "92vh" }}
          overflowY="auto"
        >
          <Wrap spacing={{ base: 2, md: 4, lg: 4 }} p={5} pb={0}>
            {Object.keys(TAG_MAPPING).map((tag) => {
              if (tag === "itineraries") {
                return (
                  <CategoryFilterMenu
                    key="itineraries"
                    tag="itineraries"
                    label="Itineraries"
                    isActive={selectedTag === "itineraries"}
                    options={ITINERARY_SUBS}
                    selections={itinSelections}
                    toggleSelection={(pref) => {
                      // 1) clear filters in the other menus
                      clearSelectionsExcept("itineraries");

                      if (pref === "all") {
                        // a) If everything's already selected, clear and go back to the "all" tab:
                        if (itinSelections.length === ITINERARY_SUBS.length) {
                          setItinSelections([]);
                          setSelectedTag("all");
                          history.replace({
                            pathname: "/ai-travel-planner/bucket-list",
                          });
                        }
                        // b) Otherwise select every pref (including the "all" placeholder)
                        else {
                          setItinSelections(ITINERARY_SUBS.map((s) => s.pref));
                          setSelectedTag("itineraries");
                          // no need pushURL, since "all" means no status filter
                        }
                      } else {
                        // toggling a single status:
                        setItinSelections((prev) => {
                          const withoutAll = prev.filter((p) => p !== "all");
                          return withoutAll.includes(pref)
                            ? withoutAll.filter((p) => p !== pref)
                            : [...withoutAll, pref];
                        });
                        setSelectedTag("itineraries");
                        // push URL only when it's a real status
                        pushURL("itineraries", pref);
                      }
                    }}
                  />
                );
              }

              /* ───────── TRANSPORTATION ───────── */
              if (tag === "transportation") {
                return (
                  <CategoryFilterMenu
                    tag="transportation"
                    label="Transportation"
                    isActive={selectedTag === "transportation"}
                    options={TRANSPORT_SUBS}
                    selections={transportPref}
                    toggleSelection={(pref) => {
                      clearSelectionsExcept("transportation");

                      if (pref === "all") {
                        // if everything is already selected, clear and go to global "all"
                        if (transportPref.length === TRANSPORT_SUBS.length) {
                          setTransportPref([]);
                          setSelectedTag("all");
                        } else {
                          // otherwise, select every transport sub-pref
                          setTransportPref(TRANSPORT_SUBS.map((s) => s.pref));
                          setSelectedTag("transportation");
                        }
                      } else {
                        // toggle a single pref, dropping "all" if present
                        setTransportPref((prev) => {
                          const withoutAll = prev.filter((p) => p !== "all");
                          return withoutAll.includes(pref)
                            ? withoutAll.filter((p) => p !== pref)
                            : [...withoutAll, pref];
                        });
                        setSelectedTag("transportation");
                      }
                    }}
                  />
                );
              }

              if (tag === "explore") {
                return (
                  <CategoryFilterMenu
                    tag="explore"
                    label="Activities"
                    isActive={selectedTag === "explore"}
                    options={EXPLORE_SUBS}
                    selections={exploreSelections}
                    toggleSelection={(pref) => {
                      clearSelectionsExcept("explore");

                      if (pref === "all") {
                        if (exploreSelections.length === EXPLORE_SUBS.length) {
                          setExploreSelections([]);
                          setSelectedTag("all");
                        } else {
                          setExploreSelections(EXPLORE_SUBS.map((s) => s.pref));
                          setSelectedTag("explore");
                        }
                      } else {
                        setExploreSelections((prev) => {
                          const withoutAll = prev.filter((p) => p !== "all");
                          return withoutAll.includes(pref)
                            ? withoutAll.filter((p) => p !== pref)
                            : [...withoutAll, pref];
                        });
                        setSelectedTag("explore");
                      }
                    }}
                  />
                );
              }

              if (tag === "accommodation") {
                return (
                  <CategoryFilterMenu
                    tag="accommodation"
                    label="Accommodation"
                    isActive={selectedTag === "accommodation"}
                    options={ACCOMMODATION_SUBS}
                    selections={accSelections}
                    toggleSelection={(pref) => {
                      clearSelectionsExcept("accommodation");

                      if (pref === "all") {
                        if (
                          accSelections.length === ACCOMMODATION_SUBS.length
                        ) {
                          setAccSelections([]);
                          setSelectedTag("all");
                        } else {
                          setAccSelections(
                            ACCOMMODATION_SUBS.map((s) => s.pref)
                          );
                          setSelectedTag("accommodation");
                        }
                      } else {
                        setAccSelections((prev) => {
                          const withoutAll = prev.filter((p) => p !== "all");
                          return withoutAll.includes(pref)
                            ? withoutAll.filter((p) => p !== pref)
                            : [...withoutAll, pref];
                        });
                        setSelectedTag("accommodation");
                      }
                    }}
                  />
                );
              }
              // ─── all other simple tags ───
              return (
                <WrapItem key={tag}>
                  <Button
                    onClick={() => {
                      setSelectedTag(tag);
                      if (tag !== "transportation") setTransportPref([]);
                      if (tag !== "explore") setExploreSelections([]);
                      if (tag !== "accommodation") setAccSelections([]);
                      if (tag === "explore")
                        setTransportPref([]) && setAccSelections([]);
                    }}
                    bg={selectedTag === tag ? "#286328" : "blackAlpha.100"}
                    color={selectedTag === tag ? "white" : "black"}
                    _hover="none"
                    borderRadius="lg"
                  >
                    <Translate
                      text={tag.charAt(0).toUpperCase() + tag.slice(1)}
                    />
                  </Button>
                </WrapItem>
              );
            })}
            <Button
              // ml="auto"
              // colorScheme="customGreen"
              // onClick={openModal}
              isDisabled={
                ["all", "itineraries", "rentalCars", "trainTickets"].includes(
                  selectedTag
                ) || !showAddButton
              }
              ml="auto"
              colorScheme="customGreen"
              onClick={openModal}
              // isDisabled={
              //   (!showAddButton,
              //   ["all", "itineraries", "rentalCars", "trainTickets"].includes(
              //     selectedTag
              //   ))
              // }
            >
              <Translate text="Add Item" />
            </Button>
          </Wrap>

          {renderAddModal()}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={5} p={5}>
            {filterItems(selectedTag).map((item) => {
              switch (item.category) {
                case "itinerary":
                  return (
                    <ItineraryCard
                      key={item._id}
                      itinerary={item.general_info}
                      status={item.status}
                      tripId={item._id}
                      ongoingTrip={ongoingTrip}
                      onToggleOngoingTrip={() => handleToggleOngoingTrip(item)}
                      handleDelete={(e) => handleDelete(e, item._id)}
                    />
                  );
                case "accommodation":
                  return (
                    <AccommodationCard
                      key={item?._id}
                      hotel={item?.data}
                      manual={item.data_saved}
                      handleDelete={(e) => handleDelete(e, item?._id)}
                      handleEdit={(accommodation) =>
                        handleEditAccommodation(accommodation, item._id)
                      }
                    />
                  );
                case "explore":
                  return (
                    <ActivityCard
                      key={item._id}
                      activity={item?.data}
                      manual={item.data_saved}
                      handleDelete={(e) => handleDelete(e, item._id)}
                      handleEdit={(activity) =>
                        handleEditActivity(activity, item._id)
                      }
                    />
                  );
                case "transportation": {
                  if (item.preference?.toLowerCase() === "rv") {
                    return (
                      <RVsCard
                        key={item._id}
                        rvs={item.data}
                        manual={item.data_saved}
                        handleDelete={(e) => handleDelete(e, item._id)}
                        handleEdit={(rv) => handleEditRV(rv, item._id)}
                      />
                    );
                  }
                  if (item.preference?.toLowerCase() === "rental_car") {
                    return (
                      <RentalCarCard
                        key={item._id}
                        rentalCar={item.data}
                        manual={item.data_saved}
                        handleDelete={(e) => handleDelete(e, item._id)}
                        handleEdit={(rentalCar) =>
                          handleEditRentalCar(rentalCar, item._id)
                        }
                      />
                    );
                  }
                  if (item.preference?.toLowerCase() === "train") {
                    return (
                      <TrainTicketsCard
                        key={item._id}
                        trainTicket={item.data}
                        manual={item.data_saved}
                        handleDelete={(e) => handleDelete(e, item._id)}
                        handleEdit={(trainTicket) =>
                          handleEditTrainTicket(trainTicket, item._id)
                        }
                      />
                    );
                  }
                  if (item.preference?.toLowerCase() === "bus") {
                    return (
                      <BusTicketCard
                        key={item._id}
                        busTicket={item.data}
                        manual={item.data_saved}
                        handleDelete={(e) => handleDelete(e, item._id)}
                        handleEdit={(busTicket) =>
                          handleEditBusTicket(busTicket, item._id)
                        }
                      />
                    );
                  }
                  if (item.preference?.toLowerCase() === "other") {
                    return (
                      <OtherTransporationCard
                        key={item._id}
                        otherTransportation={item.data}
                        manual={item.data_saved}
                        handleDelete={(e) => handleDelete(e, item._id)}
                        handleEdit={(otherTransporation) =>
                          handleEditOtherTransporation(
                            otherTransporation,
                            item._id
                          )
                        }
                      />
                    );
                  }
                  return (
                    <FlightsCard
                      key={item._id}
                      flights={item.data}
                      manual={item.data_saved}
                      handleDelete={(e) => handleDelete(e, item._id)}
                      handleEdit={(flight) =>
                        handleEditFlight(flight, item._id)
                      }
                    />
                  );
                }
                default:
                  return null;
              }
            })}
          </SimpleGrid>
        </VStack>

        <ConfirmDeleteDialog
          isOpen={isAlertOpen}
          onClose={() => setIsAlertOpen(false)}
          onConfirm={confirmDelete}
          cancelRef={cancelRef}
        />
      </Box>
    </>
  );
};

export default BucketList;
